import { QueryClientWrapper } from '@/ui/components/QueryClientWrapper';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from '@mui/material/styles';
import { ToastContextProvider } from '@payaca/ambrosia';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Router, Switch } from 'react-router';
import { IntercomProvider } from 'react-use-intercom';

import MaintenancePage from '@/ui/pages/maintenancePage/MaintenancePage';
import Splash from '@/ui/pages/splashPage/SplashPage';

import PrivateRouting from '@/routes/PrivateRouting';
import PublicRouting from '@/routes/PublicRouting';

import history from '@/services/history';

import { actions as appActions, selectors as appSelectors } from '@/api/app';
import FileUploadContextProvider from '@/context/FileUploadContext';
import AuthenticatedRootLayout from '@/ui/layouts/AuthenticatedRootLayout';

import { enableMapSet } from 'immer';
import { Helmet } from 'react-helmet';
import NotificationProvider from './ui/components/contextProviders/NotificationContextProvider';
import { ErrorBoundary } from './ui/components/errorBoundary';

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID;

// https://immerjs.github.io/immer/map-set
enableMapSet();

const theme = createTheme({
  typography: {
    //@ts-expect-error JSX -> TSX conversion
    useNextVariants: true,
    fontFamily: 'Inter',
  },
  palette: {
    //@ts-expect-error JSX -> TSX conversion
    type: 'light',
    primary: {
      main: '#263e59',
      contrastText: '#fff',
    },
  },
  overrides: {
    MuiButton: {
      containedPrimary: {
        color: '#fff',
      },
      containedSecondary: {
        color: '#fff',
      },
    },
  },
  zIndex: {
    // Copy packages/styles/tailwind.config.js
    drawer: 102,
    modal: 108,
    tooltip: 115,
  },
});

const titleEnv =
  import.meta.env.VITE_APP_ENV === 'local'
    ? ' local'
    : import.meta.env.VITE_APP_ENV === 'development'
      ? ' dev'
      : import.meta.env.VITE_APP_ENV === 'staging'
        ? ' staging'
        : '';

const App = () => {
  const dispatch = useDispatch();
  //@ts-expect-error JSX -> TSX conversion
  const loaded = useSelector((state) => appSelectors.isLoaded(state.app));
  //@ts-expect-error JSX -> TSX conversion
  const loggedIn = useSelector((state) => appSelectors.isLoggedIn(state.app));

  const [lastUnfocusAt, setLastUnfocusAt] = useState(0);

  const lastUnfocusAtRef = useRef(lastUnfocusAt);

  const setLastUnfocusAtRef = (time: any) => {
    lastUnfocusAtRef.current = time;
    setLastUnfocusAt(time);
  };

  useEffect(() => {
    dispatch(appActions.start());

    setLastUnfocusAtRef(new Date().getTime());
    // Listeners to reload page on focus if page hasnt been focused on for 24 hours
    window.addEventListener('blur', handleBlur); // set timer
    window.addEventListener('focus', handleFocus); // check timer - reload if last click was > day ago
    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  // TODO: Remove after redirect has been widely adopted
  useEffect(() => {
    if (window.location.href.includes('new.payaca')) {
      window.location.href = window.location.href.replace(
        'new.payaca',
        'web.payaca'
      );
      return;
    }
    if (window.location.href.includes('new-ui.payaca')) {
      window.location.href = window.location.href.replace(
        'new-ui.payaca',
        'staging.webapp.payaca'
      );
      return;
    }
  }, []);

  const handleBlur = () => setLastUnfocusAtRef(new Date().getTime());

  const handleFocus = () => {
    if (
      new Date().getTime() - lastUnfocusAtRef.current >=
      1000 * 60 * 60 * 24
    ) {
      // reload page if page hasnt been focused on for 24 hours
      window.location.replace(window.location.href);
    }
  };

  const currentMaintenanceSchedule = useSelector((state: any) => {
    return state.app.maintenanceSchedule?.current;
  });

  const Maintenance = useCallback(
    ({ children }: any) =>
      currentMaintenanceSchedule ? <Switch>{children}</Switch> : null,
    [currentMaintenanceSchedule]
  );

  const Loading = useCallback(
    ({ children }: any) => (!loaded ? <Switch>{children}</Switch> : null),
    [loaded]
  );

  const Unauthorized = useCallback(
    ({ children }: any) =>
      loaded && !loggedIn && !currentMaintenanceSchedule ? (
        <Switch>{children}</Switch>
      ) : null,
    [currentMaintenanceSchedule, loaded, loggedIn]
  );

  const Authorized = useCallback(
    ({ children }: any) =>
      loaded && loggedIn && !currentMaintenanceSchedule ? children : null,
    [currentMaintenanceSchedule, loaded, loggedIn]
  );

  return (
    <ErrorBoundary>
      <IntercomProvider
        appId={INTERCOM_APP_ID}
        autoBoot
        autoBootProps={{ hideDefaultLauncher: true, alignment: 'left' }}
      >
        <ToastContextProvider>
          <QueryClientWrapper>
            <NotificationProvider>
              <Helmet
                defaultTitle={`Payaca${titleEnv}`}
                titleTemplate={`Payaca${titleEnv} | %s`}
              />
              <Router history={history}>
                <MuiThemeProvider theme={theme}>
                  <div className="App">
                    <Route
                      exact
                      path="/redirectToMobile"
                      render={() => {
                        //@ts-expect-error JSX -> TSX conversion
                        window.location = 'Payaca://';
                      }}
                    />

                    <Loading>
                      <Route path="*" component={Splash} />
                    </Loading>
                    <Maintenance>
                      <Route path="*" component={MaintenancePage} />
                    </Maintenance>
                    <Unauthorized>
                      <Route path="/" component={PublicRouting} />
                    </Unauthorized>
                    <Authorized>
                      <FileUploadContextProvider>
                        <AuthenticatedRootLayout>
                          <Route path="/" component={PrivateRouting} />
                        </AuthenticatedRootLayout>
                      </FileUploadContextProvider>
                    </Authorized>
                  </div>
                </MuiThemeProvider>
              </Router>
            </NotificationProvider>
          </QueryClientWrapper>
        </ToastContextProvider>
      </IntercomProvider>
    </ErrorBoundary>
  );
};

export default App;
